import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MuiButtonAppearanceEnum, MuiModalService, MuiToastService } from '@move/ui';
import { TranslateService } from '@ngx-translate/core';
import { Observable, EMPTY, throwError, BehaviorSubject, filter } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

interface ErrorMessages {
  default: string;
  [key: number]: string;
}

const FORCE_INTERCEPT_HEADER = 'MIELE-intercept-all';
const NO_INTERCEPT_HEADER = 'MIELE-no-intercept';
const NO_TOAST_HEADER = 'MIELE-no-toasts';

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
  readonly codes = [401, 403, 404, 406, 500, 501, 502, 504];

  /* eslint-disable @typescript-eslint/naming-convention */
  readonly errorMessages: ErrorMessages = {
    default: 'global.error.request.default',
    0: 'global.error.request.offline',
    401: 'global.error.request.unauthorized',
    403: 'global.error.request.forbidden',
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  protected readonly _apiRequestRegex = new RegExp(/\/api\//);
  private isAuthenticating = false;

  constructor(
    private _translateService: TranslateService,
    private _router: Router,
    private _modalService: MuiModalService,
    private _toastService: MuiToastService
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (this.isApiRequest(req) && !req.headers.has(NO_INTERCEPT_HEADER) || req.headers.has(FORCE_INTERCEPT_HEADER)) {
      // Add interceptor to error responses
      if (this.isAuthenticating) {
        // Do not intercept and stop requests while the user is authenticating
        return EMPTY;
      }

      return next.handle(req).pipe(
        catchError((err: any) => {
          if ((err instanceof HttpErrorResponse && this.codes.includes(err.status)) || (!navigator.onLine && err.status === 0)) {
            this.handleErrorResponse(req, err);
          }

          return throwError(() => err);
        })
      );
    } else {
      // Do not intercept requests that are not API requests (e.g. static content)
      return next.handle(req);
    }
  }
  /**
   * Check if the request is an API request. All requests that contain '/api/' in the URL are considered API requests.
   * @param {HttpRequest<unknown>} req
   * @returns {boolean}
   * @private
   */
  private isApiRequest(req: HttpRequest<unknown>): boolean {
    return this._apiRequestRegex.test(req.url);
  }

  private handleErrorResponse(req: HttpRequest<unknown>, err: HttpErrorResponse): void {
      switch (err.status) {
        case 401:
          this.handle401();
          break;
        case 404:
          this.handle404();
          break;
        default:
          /* All API requests that try to write something to the backend (POST, PUT, PATCH, DELETE) are considered following some user
           * input. Therefore, an error toast should be displayed. Requests can be blacklisted by adding a special header. */
          if (
            (req.method === 'POST' || req.method === 'PUT' || req.method === 'PATCH' || req.method === 'DELETE') &&
            !req.headers.has(NO_TOAST_HEADER)
          ) {
            this.handleDefaultError(err);
          }
      }
  }

  private handleDefaultError(err: HttpErrorResponse): void {
    const key = this.errorMessages[err.status] || this.errorMessages.default;
    const msgKey = `${key}.message`;
    const titleKey = `${key}.title`;

    this._translateService.get([msgKey, titleKey], { code: err.status }).subscribe((i18n) => {
      this._toastService.danger(i18n[msgKey], i18n[titleKey], {
        closeButton: true,
        tapToDismiss: false,
      });
    });
  }

  /**
   * Error 401:
   * Display an error information that the user needs to login again and provide a link to the login page.
   */
  private handle401(): void {
    if (!this.isAuthenticating) {
      this.isAuthenticating = true;
      const key = this.errorMessages['401'] || this.errorMessages.default;
      const msgKey = `${key}.message`;
      const titleKey = `${key}.title`;
      const buttonKey = `${key}.button`;

      this._translateService.get([msgKey, titleKey, buttonKey]).subscribe((i18n) => {
        const modal = this._modalService.openBasicModal(i18n[msgKey], i18n[titleKey], {
          backdrop: 'static',
          confirmButton: {
            label: i18n[buttonKey],
            appearance: MuiButtonAppearanceEnum.DANGER,
          },
          keyboard: false,
          showCloseButton: false,
          dismissButton: false,
        });

        modal.result.then(
          () => {
            // After closing the dialog, reload location to be forced on the login page
            location.reload();
          },
          () => {
            // After dismissing (pressing the dismiss button or esc if enabled) the dialog,
            // reload location to be forced on the login page
            location.reload();
          }
        );
      });
    }
  }

  /**
   * Error 404:
   * Redirect the user to an 404 Not Found page.
   */
  private handle404(): void {
    let pageUrl = window.location.href;

    const nav = this._router.getCurrentNavigation();
    if (nav?.finalUrl) {
      pageUrl = nav.finalUrl.toString();
    }

    this._router.navigate(['/', 'error', '404'], { replaceUrl: false, skipLocationChange: true }).then();
    window.history.replaceState(null, '404', pageUrl);
  }
}
